import { useState } from "react";
import { useNavigate } from "react-router-dom";

// mui components
import { Box, Stack, Typography } from "@mui/material";

// custom components
import CustomLoader from "../../components/loader";

// custom icons
import LoganLogo from "../../assets/img/login-logo.png";

// api slice
import { useResetMutation } from "../../features/reset-password/resetApiSlice";

//utils
import { isValidEmail } from "../../utils/email-validations";

// custom styles
import styles from "./index.module.scss";
import Heading3 from "../../components/typography/heading-03";
import MessageCarousel from "../../components/message-carousel";
import Body3 from "../../components/typography/body-03";
import Heading2 from "../../components/typography/heading-02";
import Body1 from "../../components/typography/body-01";

const ResetPassword = () => {
	const navigate = useNavigate();
	const [reset, { isLoading }] = useResetMutation();

	const [systemMessages, setSystemMessages] = useState([]);

	const [email, setEmail] = useState("");
	const [error, setError] = useState("");

	const handleResetClick = async (e) => {
		e.preventDefault();

		// Prevent empty email submission
		if (!email.trim()) {
			setError("Email address can not be empty");
			return; // Stop execution if email is empty
		}

		if (!isValidEmail(email.trim())) {
			setError("Please enter a valid email address.");
			return;
		}
		setError("");

		try {
			const forgetPassowrd = await reset({
				email:email.trim(),
			}).unwrap();
			setError(forgetPassowrd?.message || "Password reset successfully.");
		} catch (err) {
			setError(err?.data?.message || "An error occurred. Please try again.");
		}
	};

	return (
		<Box className={styles.pageContainer}>
			<Box className={styles.bodyContainer}>
				<Box
					className={styles.bodyLeftContainer}
					sx={{
						display: { xs: "none", md: "flex" },
					}}
				>
					<Box className={styles.imageContainer}>
						<img
							src={LoganLogo}
							alt="logan logo"
							styles={{ width: "100%", height: "100%" }}
						/>
					</Box>
					<Stack
						flex={1}
						flexDirection="column"
						className={styles.welcomeContainer}
					>
						<Stack className={styles.contentContainer}>
							<Stack>
								<Heading3
									text="Welcome"
									fontType="bold"
									extraClass={styles.welcomeTitle}
								/>

								<Typography
									component="p"
									sx={{
										fontSize: "1.125rem",
										lineHeight: "2rem",
										fontWeight: 600,
										color: "#fff",
									}}
									className={styles.welcomeDescription}
								>
									Loan management made easy—submit, price, and lock your loans
									with real-time data and effortless control.
								</Typography>
							</Stack>

							<Stack className={styles.messageContainer}>
								<MessageCarousel data={systemMessages} />
							</Stack>
						</Stack>
					</Stack>
					<Stack className={styles.footerContainer} gap={1}>
						<Body3
							text="Please reach out to us if you are experiencing issues logging into the portal or resetting your password at:"
							fontType="semibold"
							extraClass={styles.footerTitle}
						/>
						<Stack flexDirection="row" className={styles.infoContainer}>
							<Body3 text="Email:" extraClass={styles.footerText} />
							<Body3
								text={
									<a
										href="mailto:tpoportalsupport@loganfinance.com"
										rel="noopener noreferrer"
										target="_top"
										className={styles.footerLink}
									>
										tpoportalsupport@loganfinance.com
									</a>
								}
							/>
						</Stack>
						<Stack flexDirection="row" className={styles.infoContainer}>
							<Body3 text="Phone:" extraClass={styles.footerText} />
							<Body3 text="910-302-5924" extraClass={styles.footerLink} />
						</Stack>
					</Stack>
				</Box>

				<Stack className={styles.bodyRightContainer}>
					<form onSubmit={handleResetClick} className={styles.formContainer}>
						<Stack className={styles.formBodyContainer}>
							<Heading2
								text="Reset Password"
								fontType="bold"
								extraClass={styles.resetText}
							/>

							<Box className={styles.formFieldsContainer}>
								<Box className={styles.inputFieldContainer}>
									<label htmlFor="email" className={styles.inputLabel}>
										Email
									</label>
									<input
										type="email"
										className={styles.inputField}
										id="email"
										aria-describedby="emailHelp"
										placeholder="Enter your email"
										value={email}
										onChange={(event) => {
											setEmail(event.target.value);
											setError("");
										}}
									/>
								</Box>
							</Box>

							{error !== "" && <p style={{ color: "#f00" }}>{error}</p>}
							<button
								type="submit"
								onClick={handleResetClick}
								className={styles.submitBtn}
							>
								{isLoading ? (
									<CustomLoader sx={{ color: "#fff" }} size={20} />
								) : (
									<Body3
										text="Reset Password"
										fontType="semibold"
										extraClass={styles.submitText}
									/>
								)}
							</button>
						</Stack>

						<Stack className={styles.formBodyContainer}>
							<Body1
								text="Remember your password?"
								extraClass={styles.rememberText}
							/>

							<Box
								onClick={() => navigate("/login")}
								className={styles.footerBtn}
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									marginBottom: "24px",
								}}
							>
								<Body3
									text="Login"
									fontType="semibold"
									extraClass={styles.footerText}
								/>
							</Box>
						</Stack>
					</form>
				</Stack>
			</Box>
		</Box>
	);
};

export default ResetPassword;
